import React, { useState } from 'react';
import {
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  Box,
  Divider
} from '@mui/material';
import { Check, Edit, Replay } from '@mui/icons-material';

/**
 * Display extracted OCR data with edit capability.
 * For selfie step: shows "Confirm Picture Quality" with Retake / Submit (no fields).
 */
const ExtractedDataDisplay = ({
  extractedData,
  documentId,
  onCorrect,
  onContinue,
  stepCode,
  capturedImageData,
  onRetake
}) => {
  const [editing, setEditing] = useState(false);
  const [corrections, setCorrections] = useState({});
  const [loading, setLoading] = useState(false);

  const isSelfie = stepCode === 'selfie';

  const handleFieldChange = (fieldName, value) => {
    setCorrections(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  const handleSubmitCorrections = async () => {
    if (Object.keys(corrections).length === 0) {
      // No corrections, just continue
      onContinue();
      return;
    }

    setLoading(true);
    try {
      await onCorrect(documentId, corrections);
      setEditing(false);
      onContinue();
    } catch (error) {
      console.error('Failed to submit corrections:', error);
    } finally {
      setLoading(false);
    }
  };

  const getDisplayValue = (fieldName) => {
    return corrections[fieldName] ?? extractedData[fieldName];
  };

  const renderField = (fieldName, label) => {
    if (!extractedData || !Object.prototype.hasOwnProperty.call(extractedData, fieldName)) return null;

    return (
      <Grid item xs={12} sm={6} key={fieldName}>
        <TextField
          fullWidth
          label={label}
          value={getDisplayValue(fieldName) ?? ''}
          onChange={(e) => handleFieldChange(fieldName, e.target.value)}
          disabled={!editing}
          variant={editing ? 'outlined' : 'filled'}
        />
      </Grid>
    );
  };

  if (isSelfie) {
    return (
      <Paper elevation={2} sx={{ p: 3, mt: 2 }}>
        <Typography variant="h6" gutterBottom>
          Confirm Picture Quality
        </Typography>
        {capturedImageData && (
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Box
              component="img"
              src={capturedImageData}
              alt="Selfie"
              sx={{
                maxWidth: '100%',
                maxHeight: 320,
                borderRadius: 2,
                border: '2px solid',
                borderColor: 'divider'
              }}
            />
          </Box>
        )}
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={<Replay />}
            onClick={onRetake}
          >
            Retake
          </Button>
          <Button
            variant="contained"
            startIcon={<Check />}
            onClick={onContinue}
          >
            Submit
          </Button>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper elevation={2} sx={{ p: 3, mt: 2 }}>
      <Typography variant="h6" gutterBottom>
        Extracted Information
      </Typography>

      <Alert severity="info" sx={{ mb: 2 }}>
        Please verify the information below is correct. Click Edit to make changes.
      </Alert>

      <Grid container spacing={2}>
        {renderField('full_name', 'Full Name')}
        {renderField('full_name_ar', 'Full Name (Arabic)')}
        {renderField('id_number', 'ID Number')}
        {renderField('date_of_birth', 'Date of Birth')}
        {renderField('place_of_birth', 'Place of Birth')}
        {renderField('gender', 'Gender')}
        {renderField('nationality', 'Nationality')}
        {renderField('issue_date', 'Issue Date')}
        {renderField('expiry_date', 'Expiry Date')}
        {renderField('permanent_address', 'Permanent Address')}
        {renderField('marital_status', 'Marital Status')}
        {renderField('blood_type', 'Blood Type')}
      </Grid>

      <Divider sx={{ my: 3 }} />

      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
        {!editing ? (
          <>
            {onRetake && (
              <Button
                variant="outlined"
                startIcon={<Replay />}
                onClick={onRetake}
              >
                Retake
              </Button>
            )}
            <Button
              variant="outlined"
              startIcon={<Edit />}
              onClick={() => setEditing(true)}
            >
              Edit Information
            </Button>
            <Button
              variant="contained"
              startIcon={<Check />}
              onClick={onContinue}
            >
              Information is Correct
            </Button>
          </>
        ) : (
          <>
            <Button
              variant="text"
              onClick={() => {
                setEditing(false);
                setCorrections({});
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              startIcon={<Check />}
              onClick={handleSubmitCorrections}
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Save & Continue'}
            </Button>
          </>
        )}
      </Box>
    </Paper>
  );
};

export default ExtractedDataDisplay;
